import React, { FC, useState } from "react";
import TableBasedPage from "../../templates/TableBasedPage";
import {
  Box,
  Button,
  Flex,
  Icon,
  InlineNotification,
  TableIconButton,
  Td,
  Text,
  Tr,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import { TableColumn } from "../../../types/table";
import AddNewObjectEntryYoutube from "./AddNewObjectEntryYoutube";
import { ObjectPool, YoutubeDisplayTypeIndex, YoutubeNamedEntry, YoutubeNamedEntryType } from "../../../types/Objects";
import { useObjectStore } from "../../../storez/ObjectStore";
import ImportCSVModalYoutube from "./ImportCSVModalYoutube";
import { youtubeNamedEntryTypeReverseLookup } from "./YoutubeUtils";

interface EditObjectPoolsYoutubeProps {
  breadcrumbs: { title: string; url: string; isActive: boolean }[];
  loaded: boolean;
  objectPool: ObjectPool;
}

const EditObjectPoolsYoutube: FC<EditObjectPoolsYoutubeProps> = ({ breadcrumbs, loaded, objectPool }) => {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();

  const [isAddEntryModalOpen, setIsAddEntryModalOpen] = useState(false);
  const [openCSVImportModal, setOpenCSVImportModal] = useState(false);

  const saveObject = useObjectStore((state) => state.saveObject);

  if (objectPool.id === undefined || objectPool.named_entries === undefined || objectPool.type !== YoutubeDisplayTypeIndex) {
    return null;
  }

  const columns: TableColumn[] = [
    {
      headerText: t("Type"),
      columnName: "type",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Entry"),
      columnName: "desc",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Detail"),
      columnName: "name",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Operations"),
      columnName: "operations",
      alignment: "right",
    },
  ];

  const handleOpenAddEntry = () => {
    setIsAddEntryModalOpen(true);
  };

  const csvHeaders = ["Type", "Entry"].join(",");

  const handleDownloadExampleCSV = () => {
    const csvData = [
      "Video,https://www.youtube.com/watch?v=dQw4w9WgXcQ",
      "Channel,https://www.youtube.com/channel/UC-9-kyTW8ZkZNDHQJ6FgpwQ",
      "Category,Music",
    ].join("\n");
    const csvContent = [csvHeaders, csvData].join("\n");
    const element = document.createElement("a");
    const file = new Blob([csvContent], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = "youtube-lists-template.csv";
    element.title = "Download example CSV file";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleDownloadCSV = () => {
    if (!objectPool || objectPool.named_entries === undefined) {
      return;
    }

    const csvData = (objectPool.named_entries as YoutubeNamedEntry[])
      .map((entry) => `${youtubeNamedEntryTypeReverseLookup(entry.type)},${entry.desc}`)
      .join("\n");
    const csvContent = [csvHeaders, csvData].join("\n");
    const element = document.createElement("a");
    const file = new Blob([csvContent], { type: "text/csv" });
    element.href = URL.createObjectURL(file);
    element.download = `${objectPool.name}.csv`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const headerButtons = (
    <Flex justifyContent="end">
      <Box mr="sp8">
        <Button variant="outline" data-testid="download-csv-template" onClick={handleDownloadExampleCSV}>
          {t("Download CSV Template")}
        </Button>
      </Box>

      <Box mr="sp8">
        <Button variant="primary" disabled={!loaded} data-testid="import-csv" onClick={() => setOpenCSVImportModal(true)}>
          {t("Import CSV")}
        </Button>
      </Box>

      <Box mr="sp8">
        <Button
          variant="primary"
          disabled={!loaded || objectPool?.named_entries?.length === 0}
          data-testid="export-csv"
          onClick={handleDownloadCSV}
        >
          {t("Export CSV")}
        </Button>
      </Box>

      <Box mr="sp8">
        <Button variant="primary" disabled={!loaded} data-testid="add-entries" onClick={handleOpenAddEntry}>
          {t("Edit Entries")}
        </Button>
      </Box>
    </Flex>
  );

  const youtubeEntryTypeText = (type: YoutubeNamedEntryType): string => {
    switch (type) {
      case YoutubeNamedEntryType.Video:
        return t("Video");
      case YoutubeNamedEntryType.Channel:
        return t("Channel");
      case YoutubeNamedEntryType.Category:
        return t("Category");
      default:
        return t("Unknown");
    }
  };

  const onDeleteEntry = async (entry: YoutubeNamedEntry) => {
    // Submit to backend
    const objectToSave = { ...objectPool };

    objectToSave.entries = []; // Entries will be repopulated from named_entries on the backend.
    objectToSave.named_entries = objectToSave.named_entries?.filter((e) => e.value !== entry.value);
    objectToSave.type = YoutubeDisplayTypeIndex; // This is probably already set but just in case.

    try {
      await saveObject(objectToSave);
      successToast({
        title: "Entry removed",
        description: `The entry has been successfully removed from the YouTube list.`,
        duration: 5000,
        isClosable: true,
      });
    } catch (e) {
      errorToast({
        title: "There was an error saving the YouTube entries.",
        description: "Please try again later.",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const dataMap = (entry: YoutubeNamedEntry) => {
    return (
      <Tr key={entry.value}>
        <Td>
          <Text fontWeight={400} color="text.paragraph.regular" fontSize="14px">
            {youtubeEntryTypeText(entry.type)}
          </Text>
        </Td>
        <Td>
          <Text fontWeight={400} color="text.paragraph.regular" fontSize="14px">
            {entry.desc}
          </Text>
        </Td>
        <Td>
          {entry.type !== YoutubeNamedEntryType.Category ? (
            <Text fontWeight={400} color="text.paragraph.regular" fontSize="14px">
              {entry.name}
            </Text>
          ) : (
            <></>
          )}
        </Td>
        <Td>
          <Flex justifyContent="flex-end">
            <TableIconButton
              data-testid="delete-entry"
              icon={<Icon icon="fa-trash-can" variant="solid" color="text.paragraph.light" />}
              aria-label={"Delete entry"}
              onClick={() => void onDeleteEntry(entry)}
            />
          </Flex>
        </Td>
      </Tr>
    );
  };

  return (
    <>
      <AddNewObjectEntryYoutube isOpen={isAddEntryModalOpen} onClose={() => setIsAddEntryModalOpen(false)} objectPool={objectPool} />
      <TableBasedPage
        breadcrumbs={breadcrumbs}
        title={objectPool.name || t("YouTube List")}
        loaded={loaded}
        columns={columns}
        data={objectPool.named_entries as YoutubeNamedEntry[]}
        tableDataMap={dataMap}
        childrenInTableHeader={headerButtons}
      >
        <Flex px="24" pt="24" mb="-12" w="auto" direction="column" gap="4px">
          <InlineNotification
            status={"info"}
            notificationDescription={"Please check that your configuration meets the YouTube List requirements."}
          />
          {objectPool.entries && objectPool.entries.length > 100 && (
            <InlineNotification status={"info"} notificationDescription={`Total entries: ${objectPool.entries.length}`} />
          )}
        </Flex>
      </TableBasedPage>

      <ImportCSVModalYoutube open={openCSVImportModal} onClose={() => setOpenCSVImportModal(false)} objectPool={objectPool} />
    </>
  );
};
export default EditObjectPoolsYoutube;
